/**
 * FhirExportPanel — FHIR R4 експорт + токени за болнични системи.
 *
 * Backend: /api/fhir/export, /api/fhir/tokens
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Copy, Download, Hospital, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

export default function FhirExportPanel() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [tokens, setTokens] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(null);
  const [label, setLabel] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/fhir/tokens`);
      setTokens(data?.items || []);
    } catch { /* silent */ }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/fhir/export`, { timeout: 90000 });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/fhir+json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `fhir-bundle-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Изтеглени ${data.total} FHIR ресурса.`);
    } catch {
      toast.error("Грешка при експорта.");
    } finally {
      setBusy(false);
    }
  };

  const createToken = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/fhir/tokens`, { label: label || "Болница / лекар", days: 30 });
      setFresh(data);
      setLabel("");
      load();
    } catch {
      toast.error("Грешка при създаване на токен.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await axios.delete(`${API}/fhir/tokens/${id}`);
      load();
      toast.success("Достъпът е отнет.");
    } catch { toast.error("Грешка при отнемане."); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const sub = isDark ? "text-slate-400" : "text-slate-600";
  const input = `flex-1 px-3 py-2 rounded-xl border text-sm ${
    isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-slate-300"}`;

  return (
    <div className={`p-4 rounded-2xl border ${card}`} data-testid="fhir-export-panel">
      <div className="flex items-center gap-2">
        <Hospital className="w-4 h-4 text-sky-500" />
        <h3 className="text-sm font-bold">Споделяне с болница (FHIR R4)</h3>
      </div>
      <p className={`text-[11px] mt-1 ${sub}`}>
        Изтеглете медицинското си досие в международния стандарт FHIR R4 (Patient, Observation с LOINC
        кодове, Condition с ICD-10, лекарства, алергии, документи) или дайте на лекаря си временен
        достъп до реалния endpoint.
      </p>

      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={download} disabled={busy}
          className="px-3 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-sky-500 to-blue-600 inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="fhir-download-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Изтегли FHIR Bundle
        </button>
      </div>

      <div className="mt-3 flex gap-2">
        <input className={input} placeholder="За кого е достъпът (напр. УМБАЛ Св. Иван Рилски)"
          value={label} onChange={(e) => setLabel(e.target.value)} data-testid="fhir-token-label-input" />
        <button onClick={createToken} disabled={busy}
          className={`px-3 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1 ${
            isDark ? "bg-white/10 text-slate-100" : "bg-slate-200 text-slate-800"}`}
          data-testid="fhir-create-token-btn">
          <Plus className="w-3.5 h-3.5" /> Издай токен (30 дни)
        </button>
      </div>

      {fresh && (
        <div className="mt-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30" data-testid="fhir-fresh-token">
          <div className="text-[11px] font-bold text-amber-600 dark:text-amber-400">
            Копирайте токена сега — показва се само веднъж.
          </div>
          <div className="mt-1 flex items-center gap-2">
            <code className="text-[10px] break-all flex-1">{fresh.token}</code>
            <button onClick={() => { navigator.clipboard.writeText(fresh.token); toast.success("Копирано."); }}
              className="p-1.5 rounded-md bg-amber-500/20 text-amber-600" data-testid="fhir-copy-token">
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className={`text-[10px] mt-1 ${sub}`}>
            Base URL: <code>{process.env.REACT_APP_BACKEND_URL}/api/fhir</code> · Patient ID:{" "}
            <code>{fresh.patient_id}</code> · валиден до {new Date(fresh.expires_at).toLocaleDateString("bg-BG")}
          </div>
        </div>
      )}

      {tokens.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="fhir-token-list">
          {tokens.map((t) => (
            <div key={t.id}
              className={`p-2 rounded-xl border flex items-center gap-2 ${isDark ? "border-white/10" : "border-gray-200"} ${t.revoked ? "opacity-50" : ""}`}
              data-testid={`fhir-token-${t.id}`}>
              <span className="text-[11px] font-bold flex-1 truncate">{t.label}</span>
              <span className="text-[10px] opacity-60">{t.token_preview} · {t.use_count} заявки</span>
              <span className="text-[10px] opacity-60">
                до {t.expires_at ? new Date(t.expires_at).toLocaleDateString("bg-BG") : "—"}
              </span>
              {!t.revoked && (
                <button onClick={() => revoke(t.id)} className="p-1 rounded-md text-rose-500 hover:bg-rose-500/10"
                  data-testid={`fhir-revoke-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
